var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CircularProgress } from '@material-ui/core';
import { TableChart } from '@material-ui/icons';
import { ErrorView, StyledCenterView, useTimeout } from '@sierralabs/core-ui';
import KanbanBoardCard from './KanbanBoardCard';
import { KanbanBoardProvider, useKanbanBoardContext } from './KanbanBoardContext';
import KanbanBoardRow from './KanbanBoardRow';
const StyledBoard = styled.div `
  flex: 1;
  display: flex;
  &:not(.no-columns) {
    overflow-y: hidden;
    overflow-x: auto;
  }
  .column {
    display: flex;
    flex-direction: column;
    margin: 0 10px;
    flex: 1;
    border-radius: 4px 4px 0 0;
    min-width: 188px;
  }
  .sub-column {
    height: 50%;
    display: flex;
    flex-direction: column;
    margin: 10px 0;
    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
      border-radius: 4px 4px 0 0;
    }
  }
`;
export const KanbanBoard = props => {
    const { items, columns, disabled, findEvent, setItems, onSelectItem } = props, boardProps = __rest(props, ["items", "columns", "disabled", "findEvent", "setItems", "onSelectItem"]);
    return (React.createElement(KanbanBoardProvider, { items: items, columns: columns, disabled: disabled, findEvent: findEvent, setItems: setItems, onSelectItem: onSelectItem },
        React.createElement(KanbanBoardRoot, Object.assign({}, boardProps))));
};
export const KanbanBoardRoot = props => {
    const { className, isLoading, containerProps, children } = props;
    const elementRef = useRef(null);
    const [overflow, setOverflow] = useState(false);
    const [debounceDrag, clearDebouncedDrag] = useTimeout();
    const { columns, drag, drop, findBoardItem, moveBoardItem, setDragItem, setDropItem } = useKanbanBoardContext();
    const observerRef = useRef(new ResizeObserver(() => {
        // check if overflow scrolling needed
        const element = elementRef.current;
        const columnMinWidth = 320;
        setOverflow(!!(element && element.clientWidth < columns.length * columnMinWidth));
    }));
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
    const noColumns = columns.length === 0;
    const classes = ['kanban-board'];
    className && classes.push(className);
    overflow && classes.push('overflow');
    noColumns && classes.push('no-columns');
    useEffect(() => {
        const observer = observerRef.current;
        const element = elementRef.current;
        element && observer.observe(element);
        return () => {
            element && observer.unobserve(element);
        };
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(DndContext, { sensors: sensors, onDragStart: event => {
                const { item } = findBoardItem(event.active.id);
                // delay drag to enable onClick events and
                // prevent dragging if currently dragging a task
                !drop && debounceDrag(() => setDragItem(item), 100);
            }, onDragEnd: (event) => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                clearDebouncedDrag();
                setDragItem(undefined);
                const dropColumnId = (_a = event.over) === null || _a === void 0 ? void 0 : _a.id;
                if (drag && dropColumnId) {
                    const { id, taskId, columnId } = drag.item;
                    moveBoardItem(drag.item, dropColumnId);
                    setDropItem({
                        dropColumnId: dropColumnId,
                        item: { id, taskId, columnId, index: drag.index }
                    });
                }
            }), onDragCancel: () => {
                clearDebouncedDrag();
                setDragItem(undefined);
            } },
            React.createElement(StyledBoard, Object.assign({}, containerProps, { className: classes.join(' ') }), !noColumns ? (React.createElement(KanbanBoardRow, null)) : isLoading ? (React.createElement(StyledCenterView, null,
                React.createElement("div", { className: 'content' },
                    React.createElement(CircularProgress, { size: 64, color: 'primary' })))) : (React.createElement(ErrorView, { title: 'Empty Board', description: 'There are no available items to show in board.', icon: React.createElement(TableChart, { color: 'disabled', className: 'icon' }) }))),
            React.createElement(DragOverlay, null, drag && (React.createElement(KanbanBoardCard, { isDragging: true, item: drag.item, column: drag.column })))),
        children));
};
//# sourceMappingURL=KanbanBoard.js.map